import React, { useEffect, useMemo, useState } from 'react'
import DataTable from 'react-data-table-component'
import { useAdminStoreActions, useAdminStoreData } from '../../store/adminStore'
import { Button, Card } from 'reactstrap'
import { useTranslation } from 'react-i18next'
import Icon from '../../components/icon/Icon'
import UserSubscriptionModal from './UserSubscriptionModal'
import AssignMailingPackageModal from './AssignMailingPackageModal'
import AssignPackageModal from './AssignPackageModal'

const AdminUsers = () => {
  const { fetchUsers, setUsersPage, setUsersSize, setUsersSort, setUsersFilters } = useAdminStoreActions()
  const users = useAdminStoreData.users()
  const loading = useAdminStoreData.usersLoading()
  const totalElements = useAdminStoreData.usersTotalElements()
  const page = useAdminStoreData.usersPage()
  const size = useAdminStoreData.usersSize()

  const [filterText, setFilterText] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [assignMailingModalOpen, setAssignMailingModalOpen] = useState(false)
  const [selectedAssignUser, setSelectedAssignUser] = useState(null)
  const [assignPackageModalOpen, setAssignPackageModalOpen] = useState(false)

  const { t } = useTranslation()

  useEffect(() => {
    const timer = setTimeout(() => {
      setUsersFilters({ username: filterText })
    }, 1000)

    return () => clearTimeout(timer)
  }, [filterText, setUsersFilters])

  useEffect(() => {
    fetchUsers()
  }, [])

  const handleEditClick = (row) => {
    setSelectedUser(row)
    setModalOpen(true)
  }

  const handleAssignMailingClick = (row) => {
    setSelectedAssignUser(row)
    setAssignMailingModalOpen(true)
  }

  const handleAssignPackageClick = (row) => {
    setSelectedAssignUser(row)
    setAssignPackageModalOpen(true)
  }

  const columns = useMemo(() => [
    {
      name: t('admin.table.username', 'Username'),
      selector: row => row.userName,
      sortable: true,
      sortField: 'username',
      wrap: true
    },
    {
      name: t('admin.table.email', 'Email'),
      selector: row => row.email,
      sortable: true,
      sortField: 'email',
      wrap: true
    },
    {
      name: t('admin.table.action', 'Action'),
      minWidth: '100px',
      cell: row => (
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button color="primary" size="sm" onClick={() => handleEditClick(row)} style={{ paddingInline: '.5rem' }}>
            <Icon name="edit" style={{ fontSize: '1rem', marginRight: '.625rem' }}/> {t('admin.table.edit', 'Edit')}
          </Button>
          <Button color="warning" size="sm" onClick={() => handleAssignMailingClick(row)} style={{ paddingInline: '.5rem' }} title={t('admin.table.assignMailingPackage', 'Assign Mailing Package')}>
            <Icon name="mail" style={{ fontSize: '1rem' }}/>
          </Button>
          <Button color="success" size="sm" onClick={() => handleAssignPackageClick(row)} style={{ paddingInline: '.5rem' }} title={t('admin.table.assignPackage', 'Assign Package')}>
            <Icon name="package" style={{ fontSize: '1rem' }}/>
          </Button>
        </div>
      )
    }
  ], [t])

  const handlePageChange = page => {
    setUsersPage(page - 1)
  }

  const handlePerRowsChange = (newPerPage, page) => {
    setUsersSize(newPerPage)
  }

  const handleSort = (column, sortDirection) => {
    if (column.sortField) {
      setUsersSort(`${column.sortField},${sortDirection}`)
    }
  }

  const paginationComponentOptions = {
    rowsPerPageText: t('admin.pagination.rowsPerPage', 'Rows per page:'),
    rangeSeparatorText: t('admin.pagination.rangeSeparator', 'of'),
    selectAllRowsItem: false,
    selectAllRowsItemText: 'All'
  }

  return (
    <div className="admin-users">
      <Card className="card-bordered card-preview">
        <div className="card-inner position-relative text-end">
          <div className="card-title-group">
            <div className="card-tools">
              <div className="form-inline flex-nowrap gx-3">
                <div className="form-control-wrap w-200">
                  <div className="form-icon form-icon-left">
                    <Icon name="search"/>
                  </div>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t('admin.searchPlaceholder', 'Search...')}
                    onChange={(e) => setFilterText(e.target.value)}
                    style={{ width: '20rem' }}
                  />
                </div>
              </div>
            </div>
          </div>
          <DataTable
            columns={columns}
            data={users}
            progressPending={loading}
            pagination
            paginationServer
            paginationTotalRows={totalElements}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            paginationDefaultPage={page + 1}
            paginationPerPage={size}
            paginationComponentOptions={paginationComponentOptions}
            sortServer
            onSort={handleSort}
            highlightOnHover
            pointerOnHover
            noDataComponent={<div className="p-2">{t('admin.table.noData', 'No records found')}</div>}
          />
        </div>
      </Card>
      {modalOpen && (
        <UserSubscriptionModal
          isOpen={modalOpen}
          toggle={() => setModalOpen(!modalOpen)}
          selectedUser={selectedUser}
        />
      )}
      {assignMailingModalOpen && (
        <AssignMailingPackageModal
          isOpen={assignMailingModalOpen}
          toggle={() => setAssignMailingModalOpen(!assignMailingModalOpen)}
          selectedUser={selectedAssignUser}
        />
      )}
      {assignPackageModalOpen && (
        <AssignPackageModal
          isOpen={assignPackageModalOpen}
          toggle={() => setAssignPackageModalOpen(!assignPackageModalOpen)}
          selectedUser={selectedAssignUser}
        />
      )}
    </div>
  )
}

export default AdminUsers
